import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  useRecordContext,
  TopToolbar,
  EditButton,
} from 'react-admin';
import { Box, Card, CardContent, Grid, Typography, Chip } from '@mui/material';

const ChatShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

export const ChatShow = () => {
  return (
    <Show actions={<ChatShowActions />}>
      <SimpleShowLayout>
        <ChatHeader />
        <ChatDetails />
        <EventStatistics />
      </SimpleShowLayout>
    </Show>
  );
};

const ChatHeader = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h5">{record.title}</Typography>
            {record.username && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                @{record.username}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              💬 Chat ID: {record.chat_id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              👤 Owner: {record.owner_username}
              {record.owner_first_name && ` (${record.owner_first_name})`}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Chip
                label={record.has_api_key ? 'xRocket API Configured' : 'No API Key'}
                color={record.has_api_key ? 'success' : 'default'}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const ChatDetails = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Chat Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Chat ID
            </Typography>
            <Typography variant="body1" fontFamily="monospace">
              {record.chat_id}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Username
            </Typography>
            <Typography variant="body1">
              {record.username ? `@${record.username}` : 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Owner ID
            </Typography>
            <Typography variant="body1" fontFamily="monospace">
              {record.owner_id}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Owner Username
            </Typography>
            <Typography variant="body1">
              {record.owner_username}
              {record.owner_first_name && ` (${record.owner_first_name})`}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {record.created_at
                ? new Date(record.created_at.split('.')[0]).toLocaleString()
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Last Updated
            </Typography>
            <Typography variant="body1">
              {record.updated_at
                ? new Date(record.updated_at.split('.')[0]).toLocaleString()
                : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const EventStatistics = () => {
  const record = useRecordContext();

  if (!record) return null;

  const total = record.total_events || 0;

  if (total === 0) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Event Statistics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No events created in this chat yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Event Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Total Events
            </Typography>
            <Typography variant="h4">{total}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Scheduled
            </Typography>
            <Typography variant="h4" color="info.main">
              {record.scheduled_events || 0}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Active
            </Typography>
            <Typography variant="h4" color="success.main">
              {record.active_events || 0}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Ended
            </Typography>
            <Typography variant="h4">{record.ended_events || 0}</Typography>
          </Grid>
          {(record.cancelled_events || 0) > 0 && (
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Cancelled
              </Typography>
              <Typography variant="h4" color="error.main">
                {record.cancelled_events}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
